import { useState, useCallback } from "react";
import { Paperclip, Sparkles } from "lucide-react";
import { isFogbugzEvtxAttachmentName } from "@spectree/shared";
import type { Attachment } from "@/hooks/queries/use-fogbugz";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lightbox } from "@/components/ui/lightbox";
import {
  buildAttachmentProxyUrl,
  isImageFilename,
} from "@/lib/fogbugz-attachment";
import { safeHttpUrl } from "@/lib/safe-url";
import { useAuthenticatedDownload } from "@/hooks/use-authenticated-image";
import { AttachmentThumbnail } from "./attachment-thumbnail";
import { FileTypeIcon } from "./file-type-icon";

interface AttachmentsCardProps {
  attachments: Attachment[];
  onOpenImage?: (src: string, alt: string) => void;
  /**
   * Summarize an .evtx attachment (PI-SPEC-SW-1229). When provided, each
   * downloadable .evtx row gets a Summarize button; omit it and the card
   * renders exactly as before.
   */
  onSummarizeEvtx?: (attachment: Attachment) => void;
  /**
   * `sURL` of the attachment whose summary is in flight, or null. While set,
   * every Summarize button is disabled (one summary at a time) and the
   * matching one reads "Summarizing...".
   */
  summarizingUrl?: string | null;
}

export function AttachmentsCard({
  attachments,
  onOpenImage,
  onSummarizeEvtx,
  summarizingUrl = null,
}: AttachmentsCardProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState("");
  const download = useAuthenticatedDownload();

  const handleOpen = useCallback(
    (src: string, alt: string) => {
      if (onOpenImage) {
        onOpenImage(src, alt);
      } else {
        setLightboxSrc(src);
        setLightboxAlt(alt);
      }
    },
    [onOpenImage]
  );

  const handleLightboxChange = useCallback((open: boolean) => {
    if (!open) {
      setLightboxSrc(null);
      setLightboxAlt("");
    }
  }, []);

  const images = attachments.filter((att) => isImageFilename(att.sFileName));
  const nonImages = attachments.filter(
    (att) => !isImageFilename(att.sFileName)
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5" />
            Attachments ({attachments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {images.map((att) => (
                <AttachmentThumbnail
                  key={att.sURL || att.sFileName}
                  attachment={att}
                  onOpen={handleOpen}
                />
              ))}
            </div>
          )}

          {nonImages.length > 0 && (
            <div className="space-y-2">
              {nonImages.map((att) => {
                const proxyUrl = buildAttachmentProxyUrl(att.sURL);
                const directUrl = !proxyUrl ? safeHttpUrl(att.sURL) : null;
                const downloadUrl = proxyUrl ?? directUrl;
                const canSummarize =
                  onSummarizeEvtx !== undefined && isFogbugzEvtxAttachmentName(att.sFileName);
                const isSummarizing = summarizingUrl !== null && summarizingUrl === att.sURL;
                return downloadUrl ? (
                  <div
                    key={att.sURL || att.sFileName}
                    className="flex items-center gap-2"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        proxyUrl
                          ? download(proxyUrl, att.sFileName)
                          : window.open(directUrl!, "_blank")
                      }
                      className="flex items-center gap-2 text-sm text-blue-400 hover:underline"
                    >
                      <FileTypeIcon filename={att.sFileName} />
                      {att.sFileName}
                    </button>
                    {canSummarize && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 md:h-7 px-2"
                        aria-label={
                          isSummarizing
                            ? `Summarizing ${att.sFileName}`
                            : `Summarize ${att.sFileName}`
                        }
                        disabled={summarizingUrl !== null}
                        onClick={() => {
                          onSummarizeEvtx(att);
                        }}
                      >
                        <Sparkles />
                        {isSummarizing ? "Summarizing..." : "Summarize"}
                      </Button>
                    )}
                  </div>
                ) : (
                  <span
                    key={att.sURL || att.sFileName}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                    title="Attachment URL was rejected as unsafe"
                  >
                    <FileTypeIcon filename={att.sFileName} />
                    {att.sFileName}
                  </span>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {!onOpenImage && (
        <Lightbox
          open={lightboxSrc !== null}
          onOpenChange={handleLightboxChange}
          src={lightboxSrc ?? ""}
          alt={lightboxAlt}
        />
      )}
    </>
  );
}
